import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ_SECTIONS = [
  {
    category: 'Getting Started',
    items: [
      {
        q: 'What is The Yaad Shop?',
        a: 'The Yaad Shop is a multicultural wholesale supplier based in the Greater Toronto Area. We supply tools, flags & bandanas, incense, natural products, and more to African, Caribbean, Asian, Latin American, and ethnic stores across Canada.',
      },
      {
        q: 'Is this a retail or wholesale store?',
        a: 'We are strictly B2B (wholesale only). You must apply for a wholesale account and be approved before you can view pricing or place orders. We supply businesses, not individual consumers.',
      },
      {
        q: 'How do I apply for a wholesale account?',
        a: 'Click the "Apply for Account" button in the navigation bar. Fill out the application form with your business details including your HST/Business Number. We review applications within 1-2 business days.',
      },
    ],
  },
  {
    category: 'Ordering & Products',
    items: [
      {
        q: 'How do I place a wholesale order?',
        a: 'Once your wholesale account is approved, sign in to browse our catalogue with pricing visible. Add items to your cart and complete checkout. You\'ll receive an order confirmation with payment instructions.',
      },
      {
        q: 'Is there a minimum order?',
        a: 'Products are sold per box or unit package. Each product listing shows the quantity per package. There is no separate minimum order requirement beyond the package size.',
      },
      {
        q: 'What products do you carry?',
        a: 'We carry nine product categories: Machetes, Flag Bandanas, Caps & Fishing Hats, Flag Keychains, Flag Sunglasses, Flag Hoop Earrings, Smudge Sticks, Full Size Flags, and Car Mirror Trinkets. All products are sold by the box or case — no individual pieces. Mix and match is available in select categories.',
      },
      {
        q: 'Can I request products you don\'t currently carry?',
        a: 'Absolutely. We\'re always looking to expand based on what our partners need. Contact us via email or WhatsApp with your product requests and we\'ll do our best to source them.',
      },
    ],
  },
  {
    category: 'Payment & Pricing',
    items: [
      {
        q: 'How do I pay for my order?',
        a: 'We accept Canadian Interac e-Transfer. Payment details including the e-Transfer email and memo instructions are provided at checkout and on your invoice.',
      },
      {
        q: 'Do you accept credit cards?',
        a: 'Not at this time. We currently only accept Interac e-Transfer to keep our costs low and pass those savings on to you through competitive wholesale pricing.',
      },
      {
        q: 'When is payment due?',
        a: 'Payment is due upon order placement. Orders are processed and shipped once payment is confirmed via Interac e-Transfer.',
      },
    ],
  },
  {
    category: 'Shipping & Delivery',
    items: [
      {
        q: 'Do you ship across Canada?',
        a: 'Yes! We\'re based in the Greater Toronto Area and ship Canada-wide. Shipping rates are calculated at checkout based on your location and order weight.',
      },
      {
        q: 'How long does shipping take?',
        a: 'GTA orders typically arrive within 1-3 business days. Orders to other parts of Canada generally take 3-7 business days depending on location.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Not at this time. We currently only ship within Canada. International shipping may be available in the future.',
      },
    ],
  },
];

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (question: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(question)) {
        next.delete(question);
      } else {
        next.add(question);
      }
      return next;
    });
  };

  return (
    <div className="bg-brand-warm">
      <SEO
        title="Frequently Asked Questions"
        description="Common questions about wholesale ordering from The Yaad Shop. Learn about our B2B accounts, payment via Interac e-Transfer, shipping across Canada, and more."
        path="/faq"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is The Yaad Shop?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Yaad Shop is a multicultural wholesale supplier based in the Greater Toronto Area. We supply tools, flags & bandanas, incense, natural products, and more to African, Caribbean, Asian, Latin American, and ethnic stores across Canada."
              }
            },
            {
              "@type": "Question",
              "name": "Is this a retail or wholesale store?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We are strictly B2B (wholesale only). You must apply for a wholesale account and be approved before you can view pricing or place orders. We supply businesses, not individual consumers."
              }
            },
            {
              "@type": "Question",
              "name": "How do I apply for a wholesale account?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Click the \"Apply for Account\" button in the navigation bar. Fill out the application form with your business details including your HST/Business Number. We review applications within 1-2 business days."
              }
            },
            {
              "@type": "Question",
              "name": "How do I place a wholesale order?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Once your wholesale account is approved, sign in to browse our catalogue with pricing visible. Add items to your cart and complete checkout. You'll receive an order confirmation with payment instructions."
              }
            },
            {
              "@type": "Question",
              "name": "Is there a minimum order?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Products are sold per box or unit package. Each product listing shows the quantity per package. There is no separate minimum order requirement beyond the package size."
              }
            },
            {
              "@type": "Question",
              "name": "What products do you carry?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We carry nine product categories: Machetes, Flag Bandanas, Caps & Fishing Hats, Flag Keychains, Flag Sunglasses, Flag Hoop Earrings, Smudge Sticks, Full Size Flags, and Car Mirror Trinkets. All products are sold by the box or case — no individual pieces. Mix and match is available in select categories."
              }
            },
            {
              "@type": "Question",
              "name": "Can I request products you don't currently carry?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. We're always looking to expand based on what our partners need. Contact us via email or WhatsApp with your product requests and we'll do our best to source them."
              }
            },
            {
              "@type": "Question",
              "name": "How do I pay for my order?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We accept Canadian Interac e-Transfer. Payment details including the e-Transfer email and memo instructions are provided at checkout and on your invoice."
              }
            },
            {
              "@type": "Question",
              "name": "Do you accept credit cards?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Not at this time. We currently only accept Interac e-Transfer to keep our costs low and pass those savings on to you through competitive wholesale pricing."
              }
            },
            {
              "@type": "Question",
              "name": "When is payment due?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Payment is due upon order placement. Orders are processed and shipped once payment is confirmed via Interac e-Transfer."
              }
            },
            {
              "@type": "Question",
              "name": "Do you ship across Canada?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! We're based in the Greater Toronto Area and ship Canada-wide. Shipping rates are calculated at checkout based on your location and order weight."
              }
            },
            {
              "@type": "Question",
              "name": "How long does shipping take?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "GTA orders typically arrive within 1-3 business days. Orders to other parts of Canada generally take 3-7 business days depending on location."
              }
            },
            {
              "@type": "Question",
              "name": "Do you ship internationally?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Not at this time. We currently only ship within Canada. International shipping may be available in the future."
              }
            }
          ]
        }}
      />
      {/* Page Header */}
      <section className="bg-brand-navy">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-14">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-teal-light mb-3">Help Center</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">Frequently Asked Questions</h1>
          <p className="text-base text-white/60 max-w-[560px] leading-relaxed">
            Everything you need to know about ordering wholesale from The Yaad Shop.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-10 py-20">
        <div className="max-w-3xl mx-auto space-y-12">
          {FAQ_SECTIONS.map((section) => (
            <div key={section.category}>
              <div className="text-xs font-bold uppercase tracking-[2px] text-brand-teal mb-5">{section.category}</div>
              <div className="space-y-3">
                {section.items.map((item) => {
                  const isOpen = openItems.has(item.q);
                  return (
                    <div
                      key={item.q}
                      className={`bg-white rounded-2xl border transition-all ${
                        isOpen ? 'border-brand-teal shadow-md' : 'border-gray-100 hover:border-brand-teal/50'
                      }`}
                    >
                      <button
                        onClick={() => toggleItem(item.q)}
                        className="w-full flex items-center justify-between p-6 text-left"
                        aria-expanded={isOpen}
                      >
                        <h3 className="font-display font-bold text-brand-navy pr-4">{item.q}</h3>
                        <ChevronDown
                          className={`w-5 h-5 text-brand-text-muted flex-shrink-0 transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-6 -mt-1">
                          <p className="text-sm text-brand-text-muted leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="bg-brand-light-bg">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-3xl font-extrabold text-brand-navy mb-3">Still Have Questions?</h2>
            <p className="text-base text-brand-text-muted mb-8 leading-relaxed">
              Can't find what you're looking for? Reach out to us directly — we're always happy to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <a
                href="mailto:info@theyaadshop.com"
                className="bg-brand-teal text-white px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:bg-brand-teal-dark transition-all shadow-lg shadow-brand-teal/25 hover:-translate-y-0.5 inline-block"
              >
                Email Us
              </a>
              <Link
                to="/apply"
                className="bg-white text-brand-navy border border-gray-200 px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:border-brand-teal hover:text-brand-teal transition-all"
              >
                Apply for Wholesale Account
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
